import { clerkClient } from "@/App";
import { api } from "./api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Zone interfaces based on the API schema
export type ZoneType = 'polygon' | 'circle';
export type AlertSeverityLevel = 'low' | 'medium' | 'high' | 'critical';
export type ZoneCategory = "restricted" | "warning" | "information";

export interface PolygonGeometry {
  type: 'polygon';
  coordinates: number[][][]; // Array of rings, each ring is an array of points
}

export interface CircleGeometry {
  type: 'circle';
  center: [number, number]; // [longitude, latitude]
  radius: number; // in meters
}

export type ZoneGeometry = PolygonGeometry | CircleGeometry;

export interface AlertSettings {
  enabled: boolean;
  minTimeBetweenAlerts?: number;
  severityLevel?: AlertSeverityLevel;
  customMessage?: string;
  notificationChannels?: string[];
}

export interface ZoneAccessControl {
  organizationIds?: string[];
  userIds?: string[];
  isPublic?: boolean;
}

export interface Zone {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  type: ZoneType;
  category?: ZoneCategory;
  color: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  geometry: ZoneGeometry;
  alertSettings?: AlertSettings;
  accessControl?: ZoneAccessControl;
}

export interface ZoneInput {
  name: string;
  description?: string;
  type: ZoneType;
  color: string;
  geometry: ZoneGeometry;
  category?: ZoneCategory;
  alertSettings?: AlertSettings;
  accessControl?: ZoneAccessControl;
}

export interface ZoneUpdateInput {
  name?: string;
  description?: string;
  type?: ZoneType;
  category?: ZoneCategory;
  color?: string;
  geometry?: ZoneGeometry;
  alertSettings?: AlertSettings;
  accessControl?: ZoneAccessControl;
}

export interface ZoneViolation {
  id: string;
  zoneId: string;
  detectionId: string;
  timestamp: string;
  sensorId: string;
  droneIdentifier: string;
  latitude: number;
  longitude: number;
  processedAt: string;
  alertSent: boolean;
  alertSentAt?: string;
}

export interface ZonesResponse {
  zones: Zone[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ZoneViolationsResponse {
  violations: ZoneViolation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// API functions for zones
export const fetchZones = async (
  nameContains?: string,
  limit: number = 100,
  offset: number = 0
): Promise<ZonesResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const params = new URLSearchParams();
  if (nameContains) params.append("nameContains", nameContains);
  if (limit) params.append("limit", limit.toString());
  if (offset) params.append("offset", offset.toString());
  
  const queryString = params.toString() ? `?${params.toString()}` : "";
  
  const resp = await api.get(`/v0/zones${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const useZonesQuery = () => {
  return useQuery({
    queryKey: ["zones"],
    queryFn: async () => {
      const authToken = await clerkClient.session?.getToken();
      const response = await api.get<ZonesResponse>("/v0/zones", {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data.zones;
    },
  });
};

export const fetchZone = async (zoneId: string): Promise<Zone> => {
  const authToken = await clerkClient.session?.getToken();
  
  const resp = await api.get(`/v0/zones/${zoneId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const useZoneQuery = (zoneId: string) => {
  return useQuery({
    queryKey: ["zones", zoneId],
    queryFn: () => fetchZone(zoneId),
    enabled: !!zoneId,
  });
};

export const createZone = async (zoneInput: ZoneInput): Promise<Zone> => {
  const authToken = await clerkClient.session?.getToken();
  
  const resp = await api.post("/v0/zones", zoneInput, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const useCreateZoneMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
    },
  });
};

export const updateZone = async (
  zoneId: string,
  zoneUpdateInput: ZoneUpdateInput
): Promise<Zone> => {
  const authToken = await clerkClient.session?.getToken();
  
  const resp = await api.put(`/v0/zones/${zoneId}`, zoneUpdateInput, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const useUpdateZoneMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ zoneId, zoneUpdateInput }: { zoneId: string; zoneUpdateInput: ZoneUpdateInput }) => 
      updateZone(zoneId, zoneUpdateInput),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
      queryClient.invalidateQueries({ queryKey: ["zone", variables.zoneId] });
    },
  });
};

export const deleteZone = async (zoneId: string): Promise<void> => {
  const authToken = await clerkClient.session?.getToken();
  
  await api.delete(`/v0/zones/${zoneId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

export const useDeleteZoneMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
    },
  });
};

export const fetchZoneViolations = async (
  zoneId: string,
  startTime?: string,
  endTime?: string,
  limit: number = 100,
  offset: number = 0
): Promise<ZoneViolationsResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const params = new URLSearchParams();
  if (startTime) params.append("startTime", startTime);
  if (endTime) params.append("endTime", endTime);
  if (limit) params.append("limit", limit.toString());
  if (offset) params.append("offset", offset.toString());
  
  const queryString = params.toString() ? `?${params.toString()}` : "";
  
  const resp = await api.get(`/v0/zones/${zoneId}/violations${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data;
};

export const useZoneViolationsQuery = (
  zoneId: string,
  startTime?: string,
  endTime?: string,
  limit: number = 100,
  offset: number = 0
) => {
  return useQuery({
    queryKey: ["zone-violations", zoneId, { startTime, endTime, limit, offset }],
    queryFn: () => fetchZoneViolations(zoneId, startTime, endTime, limit, offset),
    enabled: !!zoneId,
  });
}; 